/**
 * SoftAllocationPreviewModal — HITL preview + commit for SWM soft allocation.
 *
 * Reads `/allocation/soft/preview` (mark-level proposal against
 * PO-released-but-not-in-inventory qty), lets the user trim / skip
 * / bulk-adjust, then commits via `/allocation/soft/commit`.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Loader2, X, CheckCircle2, Filter, Layers, Package } from 'lucide-react';

export default function SoftAllocationPreviewModal({ projectId, onClose, onCommitted }) {
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState({ tag: '', mCode: '', priority: '' });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/v1/projects/${projectId}/swm/allocation/soft/preview`);
        const enriched = (data.proposal || []).map((r, i) => ({
          ...r, _id: `${r.markNumber}-${r.mCode}-${i}`, _skip: false, _qty: r.qty,
        }));
        setRows(enriched);
        setSelected(new Set(enriched.map(r => r._id)));
      } catch (e) {
        toast.error('Preview failed: ' + (e?.response?.data?.detail || e.message));
      }
      setLoading(false);
    })();
  }, [projectId]);

  const filtered = useMemo(() => rows.filter(r => {
    if (filter.tag && !(r.structuralTag || '').toLowerCase().includes(filter.tag.toLowerCase())) return false;
    if (filter.mCode && !(r.mCode || '').toLowerCase().includes(filter.mCode.toLowerCase())) return false;
    if (filter.priority && String(r.priority) !== String(filter.priority)) return false;
    return true;
  }), [rows, filter]);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r._id)));
  };
  const setQty = (id, qty) => setRows(rs => rs.map(r =>
    r._id === id ? { ...r, _qty: Math.max(0, Math.min(qty, r.boqQty - r.alreadySoftAlloc)) } : r));

  const bulkSkip = () => setRows(rs => rs.map(r => selected.has(r._id) ? { ...r, _skip: true } : r));
  const bulkResetToBoq = () => setRows(rs => rs.map(r =>
    selected.has(r._id) ? { ...r, _qty: Math.max(0, r.boqQty - r.alreadySoftAlloc), _skip: false } : r));
  const bulkHalve = () => setRows(rs => rs.map(r =>
    selected.has(r._id) ? { ...r, _qty: Math.max(0, Math.round(r._qty * 0.5 * 100) / 100), _skip: false } : r));

  const totalQty = filtered.filter(r => selected.has(r._id) && !r._skip)
                            .reduce((s, r) => s + Number(r._qty || 0), 0);
  const distinctTags = new Set(filtered.filter(r => selected.has(r._id) && !r._skip).map(r => r.structuralTag)).size;
  const distinctMarks = new Set(filtered.filter(r => selected.has(r._id) && !r._skip).map(r => r.markNumber)).size;

  const doCommit = async () => {
    const lines = filtered
      .filter(r => selected.has(r._id) && !r._skip && Number(r._qty) > 0)
      .map(r => ({
        structuralTag: r.structuralTag, markNumber: r.markNumber, mCode: r.mCode,
        section: r.section, fromPO: r.fromPO, poNumbers: r.poNumbers || [],
        qty: Number(r._qty), priority: r.priority, subPriority: r.subPriority,
        boqQty: r.boqQty, alreadySoftAlloc: r.alreadySoftAlloc, uom: r.uom,
      }));
    if (lines.length === 0) { toast.error('Nothing to commit.'); return; }
    setCommitting(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/swm/allocation/soft/commit`,
        { lines, note: 'Approved via soft preview modal' });
      toast.success(`Committed ${data.linesCommitted} soft allocations · ${data.marksUpdated} marks · ${data.tagsUpdated} tags updated.`);
      onCommitted?.(data);
      onClose();
    } catch (e) {
      toast.error('Commit failed: ' + (e?.response?.data?.detail || e.message));
    }
    setCommitting(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="soft-alloc-preview-modal">
      <div className="rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,.12))' }}>
        <div className="px-4 py-3 flex items-center gap-3 border-b"
          style={{ background: 'linear-gradient(90deg, #3B82F6 0%, #1E4E8C 100%)', color: '#fff' }}>
          <Package className="w-5 h-5" />
          <div className="flex-1">
            <div className="text-sm font-bold">Soft Allocation · Preview & Approve</div>
            <div className="text-[10.5px] opacity-90">
              Reserve PO-released qty (not yet in inventory) against tag → mark demand.
              Basis: orderedQty − acceptedQty (IQC). Nothing is committed until you click Approve.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="soft-alloc-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-2 flex flex-wrap items-center gap-2 border-b text-[11px]" style={{ background: '#FAFAFA' }}>
          <Filter className="w-3 h-3" style={{ color: '#64748B' }} />
          <input placeholder="Filter tag…" value={filter.tag} onChange={e => setFilter({ ...filter, tag: e.target.value })}
            className="px-2 py-1 rounded border" style={{ borderColor: 'rgba(15,23,42,.12)' }} data-testid="soft-alloc-filter-tag" />
          <input placeholder="Filter mCode…" value={filter.mCode} onChange={e => setFilter({ ...filter, mCode: e.target.value })}
            className="px-2 py-1 rounded border" style={{ borderColor: 'rgba(15,23,42,.12)' }} data-testid="soft-alloc-filter-mcode" />
          <div className="ml-2 flex items-center gap-1">
            <span className="text-[10px]" style={{ color: '#64748B' }}>Bulk on {selected.size} selected:</span>
            <button onClick={bulkResetToBoq} className="px-2 py-0.5 rounded font-semibold text-white" style={{ background: '#10B981' }} data-testid="soft-alloc-bulk-reset">Set qty = BoQ</button>
            <button onClick={bulkHalve} className="px-2 py-0.5 rounded font-semibold text-white" style={{ background: '#3B82F6' }} data-testid="soft-alloc-bulk-halve">Halve qty</button>
            <button onClick={bulkSkip} className="px-2 py-0.5 rounded font-semibold text-white" style={{ background: '#EF4444' }} data-testid="soft-alloc-bulk-skip">Skip</button>
          </div>
          <div className="ml-auto text-[10px]" style={{ color: '#64748B' }}>
            {filtered.length} proposal rows · <b>{selected.size}</b> selected · <b style={{ color: '#3B82F6' }}>{totalQty.toLocaleString()}</b> qty · {distinctTags} tags · {distinctMarks} marks
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-xs" style={{ color: '#64748B' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Generating soft proposal…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[12px]" style={{ color: '#64748B' }}>
              No soft allocation proposal — either every mark is already 100% soft-allocated or PO-released qty has all been IQC-accepted.
            </div>
          ) : (
            <table className="w-full text-[10.5px]">
              <thead className="sticky top-0 z-10" style={{ background: '#DBEAFE', color: '#1E3A8A' }}>
                <tr>
                  <th className="p-1.5 text-left w-6">
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} readOnly onClick={toggleAll} data-testid="soft-alloc-select-all" />
                  </th>
                  <th className="p-1.5 text-left uppercase text-[9px]">Tag</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">Mark</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">mCode / Section</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">From PO</th>
                  <th className="p-1.5 text-left uppercase text-[9px]">BoQ</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">Hard Alloc</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">Already Soft-Alloc</th>
                  <th className="p-1.5 text-right uppercase text-[9px]">Qty (edit)</th>
                  <th className="p-1.5 text-center uppercase text-[9px]">Priority</th>
                  <th className="p-1.5 text-center uppercase text-[9px]">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r._id} style={{
                    borderTop: '1px solid rgba(15,23,42,.08)',
                    background: r._skip ? '#FEE2E220' : (selected.has(r._id) ? '#EFF6FF' : '#fff'),
                  }} data-testid={`soft-alloc-row-${r.markNumber}-${r.mCode}`}>
                    <td className="p-1.5">
                      <input type="checkbox" checked={selected.has(r._id)} onChange={() => toggle(r._id)} data-testid={`soft-alloc-checkbox-${r.markNumber}`} />
                    </td>
                    <td className="p-1.5 font-mono font-semibold" style={{ color: '#3B82F6' }}>{r.structuralTag}</td>
                    <td className="p-1.5 font-mono">{r.markNumber}</td>
                    <td className="p-1.5">
                      <div className="font-mono font-semibold" style={{ color: '#1E4E8C' }}>{r.mCode}</div>
                      <div className="text-[9px]" style={{ color: '#64748B' }}>{r.section}</div>
                    </td>
                    <td className="p-1.5 text-[10px] font-mono" style={{ color: '#0F766E' }} title={r.fromPO}>
                      {(r.fromPO || '').length > 24 ? r.fromPO.slice(0, 24) + '…' : (r.fromPO || '—')}
                    </td>
                    <td className="p-1.5 text-right font-mono">{Number(r.boqQty || 0).toLocaleString()}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: '#22c55e' }} title="Already hard-allocated (excluded from soft alloc)">{Number(r.hardAllocatedQty || 0).toLocaleString()}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: '#94A3B8' }}>{Number(r.alreadySoftAlloc || 0).toLocaleString()}</td>
                    <td className="p-1.5 text-right">
                      <input type="number" min={0} max={r.boqQty} step={0.1} value={r._qty} onChange={e => setQty(r._id, Number(e.target.value))}
                        className="w-20 px-1 py-0.5 text-right font-mono border rounded" disabled={r._skip}
                        style={{ borderColor: 'rgba(15,23,42,.15)', background: r._skip ? '#FEE2E2' : '#fff' }}
                        data-testid={`soft-alloc-qty-${r.markNumber}`} />
                    </td>
                    <td className="p-1.5 text-center">
                      <span className="px-1.5 py-0 rounded text-[9px] font-bold" style={{ background: '#3B82F620', color: '#3B82F6' }}>
                        P{r.priority ?? '—'}·{r.subPriority ?? '—'}
                      </span>
                    </td>
                    <td className="p-1.5 text-center">
                      <button onClick={() => setRows(rs => rs.map(x => x._id === r._id ? { ...x, _skip: !x._skip } : x))}
                        className="text-[10px] font-semibold px-2 py-0.5 rounded"
                        style={{ background: r._skip ? '#EF4444' : '#F1F5F9', color: r._skip ? '#fff' : '#64748B' }}
                        data-testid={`soft-alloc-skip-${r.markNumber}`}>
                        {r._skip ? 'Skipped' : 'Skip'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-4 py-3 flex items-center gap-3 border-t" style={{ background: '#F8FAFC', borderColor: 'rgba(15,23,42,.08)' }}>
          <div className="flex items-center gap-2 text-[10.5px]" style={{ color: '#64748B' }}>
            <Layers className="w-3.5 h-3.5" style={{ color: '#3B82F6' }} />
            Priority-aware: highest-priority tag with largest remaining scope allocates first; marks are maxed 100% before advancing.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={committing}
              className="px-3 py-1.5 rounded font-semibold text-[11px] border"
              style={{ borderColor: 'rgba(15,23,42,.15)', color: '#64748B' }} data-testid="soft-alloc-cancel">
              Cancel
            </button>
            <button onClick={doCommit} disabled={committing || totalQty === 0}
              className="px-4 py-1.5 rounded font-bold text-[11px] text-white flex items-center gap-1.5 shadow"
              style={{ background: totalQty === 0 ? '#94A3B8' : '#3B82F6' }} data-testid="soft-alloc-commit">
              {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {committing ? 'Committing…' : `Commit ${filtered.filter(r => selected.has(r._id) && !r._skip && Number(r._qty) > 0).length} Soft Allocations`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
